import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, Lock, Unlock } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { downloadBlob } from "../../lib/exportPng";
import { eur } from "../../lib/shield";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../ui/alert-dialog";

const LABEL = { ready: "Ready to print", printing: "Printing", dispatched: "Dispatched", paid_out: "Paid to club" };
const e = (c) => eur(c).replace(".00", "");

export const FundraiserControl = ({ funds, onChanged }) => {
  const [state, setState] = useState(null);
  const load = () => api.get("/coach/fundraiser").then((r) => setState(r.data));
  useEffect(() => { load(); }, []);
  const act = (path, msg) => api.post(`/coach/fundraiser/${path}`).then(() => { toast.success(msg); load(); onChanged?.(); }).catch((err) => toast.error(errMsg(err)));
  const statement = (b) => api.get(`/coach/batches/${b.id}/statement.pdf`, { responseType: "blob" }).then((r) => downloadBlob(r.data, `${b.number}-statement.pdf`)).catch((err) => toast.error(errMsg(err)));
  if (!state) return null;
  return (
    <section className="border-2 border-slate-900 bg-white p-5" data-testid="coach-fundraiser-control">
      <h2 className="font-display text-lg font-extrabold uppercase">{state.closed ? "Fundraiser closed" : "Fundraiser open"}</h2>
      {state.closed ? (
        <>
          <p className="mt-1 text-sm text-slate-600" data-testid="coach-fundraiser-closed-note">Your squad links aren't taking new orders. Start a new fundraiser (e.g. next season) to reopen them – new orders form a new batch.</p>
          <button type="button" onClick={() => act("open", "New fundraiser started – squad links are open again")} className="btn-blaze mt-3 flex items-center gap-2 px-4 py-2.5 text-sm" data-testid="coach-fundraiser-open-button"><Unlock className="h-4 w-4" /> Start a new fundraiser</button>
        </>
      ) : (
        <>
          <p className="mt-1 text-sm text-slate-600">When you're ready, close the fundraiser. All paid shields from every squad are printed together and sent in one delivery, and your club payout is prepared. Players who haven't ordered are skipped.</p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button type="button" className="mt-3 flex items-center gap-2 bg-red-600 px-4 py-2.5 text-sm font-extrabold uppercase text-white hover:bg-red-700" data-testid="coach-fundraiser-close-button"><Lock className="h-4 w-4" /> Close fundraiser</button>
            </AlertDialogTrigger>
            <AlertDialogContent className="rounded-none" data-testid="coach-fundraiser-close-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Close the fundraiser?</AlertDialogTitle>
                <AlertDialogDescription>{funds?.shields || 0} paid shield{funds?.shields === 1 ? "" : "s"} across all your squads go to print as one batch. Estimated payout {e(funds?.net || 0)} after delivery. Squad links stop taking orders. Make sure your delivery address and club bank details are saved – payout waits until the bank details are verified.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="coach-fundraiser-close-cancel">Not yet</AlertDialogCancel>
                <AlertDialogAction onClick={() => act("close", "Fundraiser closed – your batch is on its way to print")} className="bg-red-600 hover:bg-red-700" data-testid="coach-fundraiser-close-confirm">Close fundraiser</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      )}
      {state.batches.length > 0 && (
        <ul className="mt-4 divide-y border-t-2 border-slate-200 text-sm" data-testid="coach-batches">
          {state.batches.map((b) => (
            <li key={b.id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`coach-batch-${b.number}`}>
              <span><span className="font-bold">{b.number}</span> · {b.totals.shields} shields · {new Date(b.created_at).toLocaleDateString("en-IE")} · paid to club {e(b.totals.net)}</span>
              <span className="flex items-center gap-2">
                <span className="bg-slate-100 px-2 py-0.5 text-xs font-bold uppercase" data-testid={`coach-batch-status-${b.number}`}>{LABEL[b.status]}</span>
                {b.status === "paid_out" && <button type="button" onClick={() => statement(b)} className="flex items-center gap-1 text-xs font-bold underline" data-testid={`coach-batch-statement-${b.number}`}><Download className="h-3.5 w-3.5" /> Statement</button>}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
